/**
 * DialogNuevoPaciente — Modal "Crear ahora, completar después" estilo Epic Express Reg.
 *
 * Flujo:
 *   1. Esenciales (visible siempre): nombre, fecha nac, sexo, teléfono, documento, email
 *   2. Detalles (acordeón opcional): dirección, ocupación, escolaridad, etc.
 *
 * Inteligencia:
 *   - Detección de duplicados en vivo (nombre/teléfono → /emr/pacientes?buscar=)
 *   - Auto-cálculo de edad inline al ingresar fecha
 *   - Vinculación con ClienteWhatsApp si el teléfono ya existe
 *   - Validación de documento por país (CURP/CC/DNI/etc.)
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Avatar, Chip, IconButton, Tooltip, Button,
  TextField, InputAdornment, CircularProgress, Dialog, Slide,
  Autocomplete, FormControl, InputLabel, Select, MenuItem, Collapse, Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  WarningAmber as WarningAmberIcon,
  CheckCircle as CheckCircleIcon,
  Cake as CakeIcon,
  WhatsApp as WhatsAppIcon,
  ChatBubbleOutline as ChatIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const API_URL = '/api';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Prefijos por país (alineados a la lista existente)
const PREFIJOS = {
  MX: '+52', CO: '+57', US: '+1', ES: '+34', PE: '+51',
  AR: '+54', CL: '+56', EC: '+593', VE: '+58', OTHER: '',
};

function calcularEdad(fechaISO) {
  if (!fechaISO) return null;
  const dob = new Date(fechaISO);
  if (isNaN(dob.getTime())) return null;
  const ms = Date.now() - dob.getTime();
  const edad = Math.floor(ms / (365.25 * 24 * 3600 * 1000));
  return edad >= 0 && edad < 130 ? edad : null;
}

const ESTADO_INICIAL = {
  nombre_completo: '',
  fecha_nacimiento: '',
  sexo: 'masculino',
  pais: 'MX',
  documento_identidad: '',
  prefijo_telefono: '+52',
  telefono: '',
  correo_electronico: '',
  // Ampliados
  nacionalidad: '',
  estado_civil: '',
  escolaridad: '',
  ocupacion: '',
  religion: '',
  lugar_nacimiento: '',
  lugar_residencia: '',
  domicilio: '',
  tipo_sangre: '',
};

export default function DialogNuevoPaciente({
  open, onClose, userId,
  modoEdicion = false,
  datosIniciales = null,        // pre-fill cuando es edición
  documentosPorPais,
  ciudadesOpciones,
  onGuardar,                    // (datos) => Promise — devuelve paciente creado/actualizado
  onAbrirExistente,             // (pacienteId) => void
}) {
  const [datos, setDatos] = useState(ESTADO_INICIAL);
  const [mostrarDetalles, setMostrarDetalles] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [duplicados, setDuplicados] = useState([]);
  const [buscandoDup, setBuscandoDup] = useState(false);
  const [whatsappVinculado, setWhatsappVinculado] = useState(null);

  // Reset al abrir o sincronizar con datos de edición
  useEffect(() => {
    if (!open) return;
    if (datosIniciales) {
      setDatos({ ...ESTADO_INICIAL, ...datosIniciales });
    } else {
      setDatos(ESTADO_INICIAL);
    }
    setMostrarDetalles(false);
    setDuplicados([]);
    setWhatsappVinculado(null);
    setError('');
  }, [open, datosIniciales]);

  const docConfig = useMemo(() => documentosPorPais[datos.pais] || documentosPorPais.OTHER, [datos.pais, documentosPorPais]);
  const edad = useMemo(() => calcularEdad(datos.fecha_nacimiento), [datos.fecha_nacimiento]);

  // Validaciones
  const docValido = !datos.documento_identidad || new RegExp(docConfig.pattern).test(datos.documento_identidad);
  const emailValido = !datos.correo_electronico || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(datos.correo_electronico);
  const telValido = !datos.telefono || /^\d{6,15}$/.test(datos.telefono.replace(/\D/g, ''));
  const camposEsencialesOk = datos.nombre_completo.trim().length >= 3
    && datos.fecha_nacimiento && edad !== null
    && datos.sexo
    && docValido && emailValido && telValido;

  // Detección de duplicados en vivo (debounced)
  const duplicadosTimer = useRef(null);
  useEffect(() => {
    if (modoEdicion) return; // En edición no buscamos duplicados
    clearTimeout(duplicadosTimer.current);

    const nombre = datos.nombre_completo.trim();
    const tel = datos.telefono.trim();
    const doc = datos.documento_identidad.trim();

    if (nombre.length < 3 && tel.length < 6 && doc.length < 4) {
      setDuplicados([]);
      return;
    }

    duplicadosTimer.current = setTimeout(async () => {
      setBuscandoDup(true);
      try {
        // Priorizamos teléfono > documento > nombre
        const term = tel.length >= 6 ? tel : (doc.length >= 4 ? doc : nombre);
        const res = await fetch(
          `${API_URL}/emr/pacientes?buscar=${encodeURIComponent(term)}&limit=5`,
          { headers: { 'X-User-Id': userId } }
        );
        if (res.ok) {
          const data = await res.json();
          setDuplicados(data.pacientes || []);
        }
      } catch (e) {
        console.warn('Búsqueda de duplicados falló:', e);
      } finally {
        setBuscandoDup(false);
      }
    }, 350);

    return () => clearTimeout(duplicadosTimer.current);
  }, [datos.nombre_completo, datos.telefono, datos.documento_identidad, modoEdicion, userId]);

  // Vinculación con ClienteWhatsApp si el teléfono ya existe
  const whatsappTimer = useRef(null);
  useEffect(() => {
    if (modoEdicion) return;
    clearTimeout(whatsappTimer.current);
    const numLimpio = (datos.prefijo_telefono + datos.telefono).replace(/\D/g, '');
    if (numLimpio.length < 8) {
      setWhatsappVinculado(null);
      return;
    }
    whatsappTimer.current = setTimeout(async () => {
      try {
        const num = encodeURIComponent(`+${numLimpio}`);
        const res = await fetch(`${API_URL}/clientes-whatsapp/numero/${num}`, {
          headers: { 'X-User-Id': userId },
        });
        if (res.ok) {
          const cliente = await res.json();
          setWhatsappVinculado(cliente);
        } else {
          setWhatsappVinculado(null);
        }
      } catch (_) {
        setWhatsappVinculado(null);
      }
    }, 500);
    return () => clearTimeout(whatsappTimer.current);
  }, [datos.telefono, datos.prefijo_telefono, modoEdicion, userId]);

  const set = useCallback((cambios) => setDatos(prev => ({ ...prev, ...cambios })), []);

  // Cuando cambia país, ajusta prefijo telefónico
  useEffect(() => {
    const pref = PREFIJOS[datos.pais];
    if (pref && datos.prefijo_telefono !== pref) {
      set({ prefijo_telefono: pref });
    }
  }, [datos.pais]); // eslint-disable-line

  const handleGuardar = async () => {
    if (!camposEsencialesOk) return;
    setGuardando(true);
    setError('');
    try {
      await onGuardar(datos);
    } catch (e) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  // ----------------- RENDER -----------------
  return (
    <Dialog
      open={open}
      onClose={() => !guardando && onClose()}
      maxWidth="sm"
      fullWidth
      TransitionComponent={SlideUp}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header slim */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.2,
        py: 1, pl: 2, pr: 1,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
        color: '#fff',
      }}>
        <PersonIcon sx={{ fontSize: 18, color: '#fbbf24' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.2, lineHeight: 1.1 }}>
            {modoEdicion ? 'Editar paciente' : 'Nuevo paciente'}
          </Typography>
          <Typography sx={{ fontSize: 10.5, opacity: 0.75 }}>
            {modoEdicion ? 'Actualizar ficha de identificación' : 'Crea ahora · completa los detalles después'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={guardando}
          sx={{ color: 'rgba(255,255,255,0.85)', p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.12)' } }}
        >
          <CloseIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, pt: 1.5, maxHeight: '76vh', overflowY: 'auto' }}>

        {/* Banner de duplicados */}
        {!modoEdicion && duplicados.length > 0 && (
          <DuplicadosBanner
            duplicados={duplicados.slice(0, 3)}
            telefonoActual={datos.telefono.trim()}
            documentoActual={datos.documento_identidad.trim()}
            onAbrirExistente={(p) => { onClose(); onAbrirExistente?.(p); }}
          />
        )}

        {/* Banner de WhatsApp vinculado */}
        {!modoEdicion && whatsappVinculado && (
          <Box sx={{
            mb: 1.5, p: 1.2, borderRadius: 2,
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            border: '1px solid #6ee7b7',
            display: 'flex', alignItems: 'center', gap: 1.2,
          }}>
            <WhatsAppIcon sx={{ color: '#059669', fontSize: 22 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#065f46', mb: 0.1 }}>
                Este teléfono ya conversa por WhatsApp con NARA
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#047857' }}>
                {whatsappVinculado.nombre || 'Cliente registrado'} · se vinculará automáticamente al crear el expediente
              </Typography>
            </Box>
          </Box>
        )}

        {/* SECCIÓN ESENCIALES */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            sx={inputStyle}
            label="Nombre completo"
            placeholder="Ej: Juan Pérez García"
            value={datos.nombre_completo}
            onChange={(e) => set({ nombre_completo: e.target.value })}
            required size="small" autoFocus
            InputProps={{
              endAdornment: buscandoDup && <CircularProgress size={14} sx={{ mr: 1 }} />,
            }}
          />

          {/* Fecha + edad calculada inline + sexo */}
          <Box sx={{ display: 'flex', gap: 1.2 }}>
            <TextField
              sx={{ ...inputStyle, flex: 1.6 }}
              type="date"
              label="Fecha de nacimiento"
              value={datos.fecha_nacimiento ? String(datos.fecha_nacimiento).slice(0, 10) : ''}
              onChange={(e) => set({ fecha_nacimiento: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required size="small"
              InputProps={{
                endAdornment: edad !== null && (
                  <InputAdornment position="end">
                    <Chip
                      icon={<CakeIcon sx={{ fontSize: 12, color: '#1e3a5f !important' }} />}
                      label={`${edad} años`} size="small"
                      sx={{ background: '#eff6ff', color: '#1e3a5f', fontWeight: 700, fontSize: 11, height: 22 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ ...inputStyle, flex: 1 }}>
              <InputLabel shrink>Sexo</InputLabel>
              <Select
                label="Sexo" notched
                value={datos.sexo}
                onChange={(e) => set({ sexo: e.target.value })}
              >
                <MenuItem value="masculino">Masculino</MenuItem>
                <MenuItem value="femenino">Femenino</MenuItem>
                <MenuItem value="otro">Otro</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Teléfono: país + número */}
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
            <FormControl size="small" sx={{ ...inputStyle, minWidth: 130 }}>
              <InputLabel shrink>País</InputLabel>
              <Select
                label="País" notched
                value={datos.pais}
                onChange={(e) => set({ pais: e.target.value })}
              >
                {Object.entries(documentosPorPais).map(([code, c]) => (
                  <MenuItem key={code} value={code}>{c.nombre}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              sx={{ ...inputStyle, flex: 1 }}
              label="Teléfono"
              placeholder="3001234567"
              value={datos.telefono}
              onChange={(e) => set({ telefono: e.target.value.replace(/[^\d\s-]/g, '') })}
              size="small"
              error={!telValido}
              helperText={!telValido ? 'Número inválido' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                      {datos.prefijo_telefono}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Documento + correo */}
          <Box sx={{ display: 'flex', gap: 1.2 }}>
            <TextField
              sx={{ ...inputStyle, flex: 1 }}
              label={`${docConfig.documento} (opcional)`}
              placeholder={docConfig.placeholder}
              value={datos.documento_identidad}
              onChange={(e) => set({ documento_identidad: e.target.value.toUpperCase().slice(0, docConfig.maxLength) })}
              size="small"
              error={!docValido}
              helperText={!docValido ? `Formato esperado: ${docConfig.placeholder}` : ''}
            />
            <TextField
              sx={{ ...inputStyle, flex: 1.2 }}
              type="email"
              label="Correo electrónico (opcional)"
              placeholder="paciente@ejemplo.com"
              value={datos.correo_electronico}
              onChange={(e) => set({ correo_electronico: e.target.value })}
              size="small"
              error={!emailValido}
              helperText={!emailValido ? 'Correo inválido' : ''}
            />
          </Box>
        </Box>

        {/* ACORDEÓN DE DETALLES AMPLIADOS */}
        <Box sx={{ mt: 2 }}>
          <Button
            fullWidth size="small"
            onClick={() => setMostrarDetalles(v => !v)}
            startIcon={mostrarDetalles ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              textTransform: 'none', fontSize: 12, fontWeight: 600,
              color: '#475569', justifyContent: 'flex-start',
              borderRadius: 2, py: 0.8,
              borderTop: '1px dashed #e2e8f0', borderBottom: '1px dashed #e2e8f0',
              '&:hover': { background: '#f8fafc' },
            }}
          >
            {mostrarDetalles ? 'Ocultar detalles ampliados' : 'Agregar más datos (opcional)'}
            <Typography sx={{ ml: 'auto', fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic' }}>
              Editables luego desde Antecedentes
            </Typography>
          </Button>

          <Collapse in={mostrarDetalles} timeout={250}>
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1.2 }}>
                <TextField sx={{ ...inputStyle, flex: 1 }} size="small" label="Ocupación"
                  value={datos.ocupacion} onChange={(e) => set({ ocupacion: e.target.value })} />
                <FormControl sx={{ ...inputStyle, flex: 1 }} size="small">
                  <InputLabel shrink>Estado civil</InputLabel>
                  <Select label="Estado civil" notched
                    value={datos.estado_civil}
                    onChange={(e) => set({ estado_civil: e.target.value })}
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="soltero">Soltero(a)</MenuItem>
                    <MenuItem value="casado">Casado(a)</MenuItem>
                    <MenuItem value="union_libre">Unión libre</MenuItem>
                    <MenuItem value="viudo">Viudo(a)</MenuItem>
                    <MenuItem value="divorciado">Divorciado(a)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.2 }}>
                <TextField sx={{ ...inputStyle, flex: 1 }} size="small" label="Escolaridad"
                  value={datos.escolaridad} onChange={(e) => set({ escolaridad: e.target.value })} />
                <TextField sx={{ ...inputStyle, flex: 1 }} size="small" label="Religión"
                  value={datos.religion} onChange={(e) => set({ religion: e.target.value })} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.2 }}>
                <Autocomplete
                  freeSolo size="small" sx={{ ...inputStyle, flex: 1 }}
                  options={ciudadesOpciones || []}
                  value={datos.lugar_nacimiento}
                  onChange={(_, v) => set({ lugar_nacimiento: v || '' })}
                  onInputChange={(_, v) => set({ lugar_nacimiento: v })}
                  renderInput={(p) => <TextField {...p} label="Lugar de nacimiento" />}
                />
                <Autocomplete
                  freeSolo size="small" sx={{ ...inputStyle, flex: 1 }}
                  options={ciudadesOpciones || []}
                  value={datos.lugar_residencia}
                  onChange={(_, v) => set({ lugar_residencia: v || '' })}
                  onInputChange={(_, v) => set({ lugar_residencia: v })}
                  renderInput={(p) => <TextField {...p} label="Lugar de residencia" />}
                />
              </Box>
              <TextField sx={inputStyle} size="small" label="Domicilio"
                placeholder="Calle, número, colonia, ciudad..."
                value={datos.domicilio} onChange={(e) => set({ domicilio: e.target.value })} />
              <Box sx={{ display: 'flex', gap: 1.2 }}>
                <FormControl sx={{ ...inputStyle, flex: 1 }} size="small">
                  <InputLabel shrink>Tipo de sangre</InputLabel>
                  <Select label="Tipo de sangre" notched
                    value={datos.tipo_sangre}
                    onChange={(e) => set({ tipo_sangre: e.target.value })}
                  >
                    <MenuItem value="">—</MenuItem>
                    {['O+','O-','A+','A-','B+','B-','AB+','AB-'].map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField sx={{ ...inputStyle, flex: 1 }} size="small" label="Nacionalidad"
                  value={datos.nacionalidad} onChange={(e) => set({ nacionalidad: e.target.value })} />
              </Box>
            </Box>
          </Collapse>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1.5, py: 0.4, fontSize: 12 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Acciones */}
      <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 0.5, borderTop: '1px solid #f1f5f9' }}>
        <Button
          fullWidth onClick={onClose} disabled={guardando}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 12.5,
            borderRadius: 2, py: 1, color: '#475569', background: '#f1f5f9',
            '&:hover': { background: '#e2e8f0' },
          }}
        >
          Cancelar
        </Button>
        <Button
          fullWidth variant="contained"
          onClick={handleGuardar}
          disabled={!camposEsencialesOk || guardando}
          startIcon={guardando ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 12.5,
            borderRadius: 2, py: 1,
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            boxShadow: '0 4px 12px rgba(30, 58, 95, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)',
              boxShadow: '0 6px 16px rgba(30, 58, 95, 0.4)',
            },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
          }}
        >
          {guardando
            ? 'Guardando...'
            : modoEdicion
              ? 'Guardar cambios'
              : 'Crear y abrir expediente'}
        </Button>
      </Box>
    </Dialog>
  );
}

// Estilos comunes para inputs (mismo theme del proyecto)
const inputStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2, background: '#f8fafc', fontSize: 13.5,
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#1e3a5f', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12.5, fontWeight: 600, color: '#475569' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' },
};

// ============================================================================
// Sub-componente: Banner de duplicados
// ============================================================================
function DuplicadosBanner({ duplicados, telefonoActual, documentoActual, onAbrirExistente }) {
  // Determinar nivel de confianza
  const top = duplicados[0];
  const matchExacto = (
    (telefonoActual && (top.telefono || '').replace(/\D/g, '').includes(telefonoActual.replace(/\D/g, ''))) ||
    (documentoActual && (top.curp || '').toUpperCase() === documentoActual.toUpperCase())
  );

  const nivelColor = matchExacto
    ? { bg: '#fef2f2', border: '#fca5a5', textTitle: '#991b1b', text: '#7f1d1d', accent: '#dc2626' }
    : { bg: '#fffbeb', border: '#fde68a', textTitle: '#92400e', text: '#78350f', accent: '#d97706' };

  return (
    <Box sx={{
      mb: 1.5, p: 1.2, borderRadius: 2,
      background: nivelColor.bg,
      border: `1px solid ${nivelColor.border}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
        <WarningAmberIcon sx={{ color: nivelColor.accent, fontSize: 18 }} />
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: nivelColor.textTitle, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          {matchExacto ? 'Paciente probablemente ya registrado' : 'Posible duplicado'}
        </Typography>
        <Typography sx={{ fontSize: 11, color: nivelColor.text, ml: 'auto' }}>
          {duplicados.length} coincidencia{duplicados.length > 1 ? 's' : ''}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {duplicados.map((p) => {
          const inicial = (p.nombre_completo || '?').charAt(0).toUpperCase();
          const edad = calcularEdad(p.fecha_nacimiento);
          return (
            <Box key={p.id} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: 0.7, borderRadius: 1.5, background: '#fff',
              border: '1px solid rgba(0,0,0,0.04)',
            }}>
              <Avatar sx={{ width: 26, height: 26, bgcolor: nivelColor.bg, color: nivelColor.accent, fontSize: 11, fontWeight: 800 }}>
                {inicial}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.nombre_completo}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                  {edad !== null ? `${edad}a · ` : ''}
                  {p.sexo ? p.sexo.charAt(0).toUpperCase() + ' · ' : ''}
                  {p.telefono || 'sin tel'}
                  {p.numero_expediente ? ` · ${p.numero_expediente}` : ''}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => onAbrirExistente(p)}
                sx={{
                  textTransform: 'none', fontSize: 11, fontWeight: 700,
                  color: nivelColor.accent, borderRadius: 1.5, py: 0.3, px: 1,
                  '&:hover': { background: nivelColor.bg },
                }}
              >
                Abrir
              </Button>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
